/**
 * Serializer for Search History API to handle data transformation between frontend and Flask backend
 */
class SearchHistorySerializer {
  fromJSON(apiData) {
    const entry = apiData.data || apiData;

    if (!entry) {
      return null;
    }

    const frontendEntry = {
      id: entry.id,
      user_id: entry.user_id,
      search_query: entry.search_query || entry.query || "",
      results_count: entry.results_count || 0,
      search_filters: entry.search_filters || null,
      search_sort: entry.search_sort || null,
      search_scope: entry.search_scope || "all",
      response_time_ms: entry.response_time_ms || null,
      success: entry.success !== undefined ? entry.success : true,
      error_message: entry.error_message || null,

      // Date fields
      created_at: entry.created_at,
      updated_at: entry.updated_at,

      // Frontend-specific fields
      query: entry.search_query || entry.query || "",
      timestamp: entry.created_at,
      resultsCount: entry.results_count || 0,
      time_ago: entry.time_ago || null,
      is_recent: entry.is_recent || false,

      metadata: {
        created_at: entry.created_at,
        updated_at: entry.updated_at,
        response_time_ms: entry.response_time_ms,
        success: entry.success,
      },
    };

    return frontendEntry;
  }

  toJSON(frontendData) {
    const backendEntry = {
      search_query: frontendData.search_query || frontendData.query || "",
      results_count:
        frontendData.results_count || frontendData.resultsCount || 0,
      search_filters: frontendData.search_filters || null,
      search_sort: frontendData.search_sort || null,
      search_scope: frontendData.search_scope || "all",
      response_time_ms: frontendData.response_time_ms || null,
      success: frontendData.success !== undefined ? frontendData.success : true,
      error_message: frontendData.error_message || null,

      // User context
      user_id: frontendData.user_id,
    };

    // Remove null/undefined values
    Object.keys(backendEntry).forEach((key) => {
      if (backendEntry[key] === null || backendEntry[key] === undefined) {
        delete backendEntry[key];
      }
    });

    return backendEntry;
  }

  /**
   * Transform search response format
   */
  fromSearchJSON(searchResponse) {
    if (!searchResponse || !searchResponse.data) {
      return { hits: [], total: 0, pagination: null };
    }

    const data = searchResponse.data;

    // Handle paginated response
    if (data.items && Array.isArray(data.items)) {
      return {
        hits: data.items.map((item) => this.fromJSON(item)),
        total: data.pagination?.total || data.items.length,
        pagination: data.pagination,
      };
    }

    // Handle direct array response
    if (Array.isArray(data)) {
      return {
        hits: data.map((item) => this.fromJSON(item)),
        total: data.length,
        pagination: null,
      };
    }

    // Handle nested data response
    if (data.data && Array.isArray(data.data)) {
      return {
        hits: data.data.map((item) => this.fromJSON(item)),
        total: data.total || data.data.length,
        pagination: data.pagination,
      };
    }

    return { hits: [], total: 0, pagination: null };
  }

  /**
   * Transform stats response format
   */
  fromStatsJSON(statsResponse) {
    if (!statsResponse || !statsResponse.data) {
      return null;
    }

    const data = statsResponse.data;

    return {
      user_id: data.user_id,
      period_days: data.period_days || 30,
      total_searches: data.total_searches || 0,
      successful_searches: data.successful_searches || 0,
      success_rate: data.success_rate || 0,
      average_per_day: data.average_per_day || 0,
      popular_searches: data.popular_searches || [],
      recent_searches: data.recent_searches || [],
    };
  }

  /**
   * Transform popular searches for frontend display
   */
  fromPopularSearchesJSON(popularSearches) {
    if (!Array.isArray(popularSearches)) {
      return [];
    }

    return popularSearches.map((search) => ({
      query: search.search_query,
      search_count: search.search_count || 0,
      last_searched: search.last_searched,
      time_ago: search.time_ago,
    }));
  }

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp) {
    if (!timestamp) return "";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return "";
      }

      return date.toLocaleString();
    } catch (error) {
      return "";
    }
  }

  /**
   * Get relative time string (e.g., "2 hours ago")
   */
  getRelativeTime(timestamp) {
    if (!timestamp) return "";

    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return "";
      }

      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) return "Just now";
      if (diffInMinutes < 60)
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24)
        return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7)
        return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;

      const diffInWeeks = Math.floor(diffInDays / 7);
      if (diffInWeeks < 4)
        return `${diffInWeeks} week${diffInWeeks !== 1 ? "s" : ""} ago`;

      const diffInMonths = Math.floor(diffInDays / 30);
      return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
    } catch (error) {
      return "";
    }
  }

  /**
   * Sort search history entries
   */
  sortHistory(history, sortBy = "date", sortOrder = "desc") {
    if (!Array.isArray(history)) {
      return [];
    }

    const sorted = [...history].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "query":
          aVal = (a.search_query || a.query || "").toLowerCase();
          bVal = (b.search_query || b.query || "").toLowerCase();
          break;
        case "results_count":
          aVal = a.results_count || a.resultsCount || 0;
          bVal = b.results_count || b.resultsCount || 0;
          break;
        case "response_time":
          aVal = a.response_time_ms || 0;
          bVal = b.response_time_ms || 0;
          break;
        case "date":
        default:
          aVal = new Date(a.created_at || a.timestamp);
          bVal = new Date(b.created_at || b.timestamp);
          break;
      }

      if (sortOrder === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return sorted;
  }

  /**
   * Filter search history entries
   */
  filterHistory(history, filters = {}) {
    if (!Array.isArray(history)) {
      return [];
    }

    let filtered = [...history];

    // Filter by search term
    if (filters.search_term) {
      const term = filters.search_term.toLowerCase();
      filtered = filtered.filter((entry) =>
        (entry.search_query || entry.query || "").toLowerCase().includes(term)
      );
    }

    // Filter by success status
    if (filters.success_only !== undefined) {
      filtered = filtered.filter(
        (entry) => entry.success === filters.success_only
      );
    }

    // Filter by minimum results
    if (filters.min_results !== undefined) {
      filtered = filtered.filter(
        (entry) =>
          (entry.results_count || entry.resultsCount || 0) >=
          filters.min_results
      );
    }

    // Filter by date range
    if (filters.days) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - filters.days);

      filtered = filtered.filter((entry) => {
        const entryDate = new Date(entry.created_at || entry.timestamp);
        return entryDate >= cutoffDate;
      });
    }

    return filtered;
  }

  /**
   * Group search history entries by date
   */
  groupByDate(history) {
    if (!Array.isArray(history)) {
      return {};
    }

    const grouped = {};

    history.forEach((entry) => {
      const date = new Date(entry.created_at || entry.timestamp);
      const dateKey = date.toISOString().split("T")[0]; // YYYY-MM-DD format

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }

      grouped[dateKey].push(entry);
    });

    return grouped;
  }

  /**
   * Get search suggestions from history
   */
  getSearchSuggestions(history, query, limit = 5) {
    if (!Array.isArray(history) || !query) {
      return [];
    }

    const lowerQuery = query.toLowerCase();
    const suggestions = [];
    const seen = new Set();

    for (const entry of history) {
      const searchQuery = entry.search_query || entry.query || "";
      const lowerSearchQuery = searchQuery.toLowerCase();

      if (
        lowerSearchQuery.includes(lowerQuery) &&
        !seen.has(lowerSearchQuery) &&
        entry.success !== false
      ) {
        seen.add(lowerSearchQuery);
        suggestions.push({
          query: searchQuery,
          results_count: entry.results_count || entry.resultsCount || 0,
          last_used: entry.created_at || entry.timestamp,
          time_ago: this.getRelativeTime(entry.created_at || entry.timestamp),
        });

        if (suggestions.length >= limit) {
          break;
        }
      }
    }

    return suggestions;
  }

  /**
   * Validate search history entry data
   */
  validateEntry(data) {
    const errors = [];

    if (!data.search_query || typeof data.search_query !== "string") {
      errors.push("Search query is required and must be a string");
    }

    if (data.search_query && data.search_query.trim().length === 0) {
      errors.push("Search query cannot be empty");
    }

    if (
      data.results_count !== undefined &&
      (typeof data.results_count !== "number" || data.results_count < 0)
    ) {
      errors.push("Results count must be a non-negative number");
    }

    if (
      data.response_time_ms !== undefined &&
      (typeof data.response_time_ms !== "number" || data.response_time_ms < 0)
    ) {
      errors.push("Response time must be a non-negative number");
    }

    if (data.success !== undefined && typeof data.success !== "boolean") {
      errors.push("Success must be a boolean value");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export const serializer = new SearchHistorySerializer();
export default SearchHistorySerializer;
